import * as d3 from 'd3';
import Vue from 'vue';
import plugin, { VVsCodeExtension } from '@appland/components';
import { loadFont } from '@/appland/util';
import scenarioEditUI from '@/ui/scenarioEdit';
import scenarioShareUI from '@/ui/scenarioShare';

const GIT_HOST_REGEX = new RegExp(
  [
    '(?<protocol>(https?://|ssh://|git://))*',
    '(?<login>[^@]+@)*',
    '(?<host>[^/:]+)',
    '(?<port>:\\d*)?',
    '(:|/)',
    '(?<path>.*)',
  ].join('')
);

function getSourceUrl(repository, commit, location) {
  if (!repository || !commit || !location || location.startsWith('/')) {
    return;
  }

  const match = repository.replace(/(\.git)?\/?$/, '').match(GIT_HOST_REGEX);
  if (!match) {
    return;
  }

  const { host, path } = match.groups;
  let sourcePath = location;
  let sourceUrl;
  if (host.endsWith('github.com')) {
    sourcePath = sourcePath.replace(/:(\d+)$/, '#L$1');
    sourceUrl = `https://github.com/${path}/blob/${commit}/${sourcePath}`;
  } else if (host.endsWith('gitlab.com')) {
    sourcePath = sourcePath.replace(/:(\d+)$/, '#L$1');
    sourceUrl = `https://gitlab.com/${path}/-/blob/${commit}/${sourcePath}`;
  } else if (host.endsWith('bitbucket.org')) {
    sourcePath = sourcePath.replace(/:(\d+)$/, '#lines-$1');
    sourceUrl = `https://bitbucket.org/${path}/src/${commit}/${sourcePath}`;
  }

  return sourceUrl;
}

// Exposed for testing
window.getSourceUrl = getSourceUrl;

Vue.use(plugin);

const documentLoaded = new Promise((resolve) =>
  document.addEventListener('DOMContentLoaded', resolve)
);
const fontLoaded = loadFont('IBM Plex Mono', '/fonts/IBMPlexMono/IBMPlexMono-Medium.ttf');
Promise.all([documentLoaded, fontLoaded]).then(async () => {
  const appmapViewer = new Vue({
    render: (h) => h(VVsCodeExtension, { ref: 'ui' }),
    methods: {
      loadData(text) {
        this.$refs.ui.loadData(text);
      },
      setSelectedObject(fqid) {
        this.$refs.ui.setSelectedObject(fqid);
      },
      getState() {
        return this.$refs.ui.getState();
      },
      setState(state) {
        this.$refs.ui.setState(state);
      },
    },
  });

  appmapViewer.$mount();
  appmapViewer.loadData(window.scenarioData.data);

  appmapViewer.$on('request-resolve-location', (location) => {
    const { git } = window.scenarioData.data.metadata;
    let externalUrl;
    if (git) {
      externalUrl = getSourceUrl(git.repository, window.mapsetCommit || git.commit, location);
    }
    appmapViewer.$emit('response-resolve-location', {
      location,
      externalUrl,
    });
  });

  appmapViewer.$on('viewSource', ({ externalUrl }) => {
    if (externalUrl) window.open(externalUrl, '_blank');
  });

  appmapViewer.$on('stateChanged', () => {
    let path = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);

    searchParams.set('state', encodeURI(appmapViewer.getState()));

    window.history.replaceState(null, null, (path += `?${searchParams.toString()}`));
  });

  let state = null;
  const searchParams = new URLSearchParams(window.location.search);
  const savedState = searchParams.get('state');
  if (savedState) {
    state = decodeURI(savedState);
  }

  if (state) {
    window.requestAnimationFrame(() => {
      appmapViewer.setState(state);
    });
  }

  const scenarioAppmap = document.querySelector('#scenario-appmap');
  scenarioAppmap.style.maxHeight = `calc(100vh - ${
    document.querySelector('header').offsetHeight
  }px)`;
  scenarioAppmap.appendChild(appmapViewer.$el);

  scenarioEditUI(d3.select('#edit-modal'), d3.select('#edit-scenario-confirm')).scenario(
    () => window.scenarioData
  )();

  scenarioShareUI(d3.select('#share-modal')).scenario(() => window.scenarioData)();
});
